/**
 * Mail servis — wrapper za PHP /mailer.php endpoint na utils.infinitycreative.agency.
 *
 * Koristi isti API URL i auth token kao mediaUploader.js (REACT_APP_MEDIA_API_URL / TOKEN).
 *
 * Primjer:
 *   import { sendMail } from '../services/mailService';
 *   import { contactReceivedAdmin } from '../services/emailTemplates';
 *
 *   const tpl = contactReceivedAdmin({ name, email, message, ... });
 *   await sendMail({ to: 'admin@infinitycreative.agency', ...tpl, replyTo: email });
 *
 * Greške se ne propagiraju — ovaj servis "best effort" (failure ne smije srušiti
 * korisnikov flow). Loguje se u console.error za debug.
 */

const API_URL = (process.env.REACT_APP_MEDIA_API_URL || '').replace(/\/$/, '');
const API_TOKEN = process.env.REACT_APP_MEDIA_API_TOKEN || '';

export const isMailerConfigured = () => Boolean(API_URL && API_TOKEN);

/**
 * Encode UTF-8 string u Base64 (browser-safe — handle-uje multi-byte karaktere
 * poput č, ć, š, ž, đ koje obični btoa() ne podnosi).
 *
 * Ovo je nužno jer cPanel ModSecurity WAF blokira POST request-e koji u JSON
 * body-ju imaju puno HTML tagova (<table>, <a href>, inline style itd).
 * Encode-ujemo HTML u base64 → ModSec vidi nevini base64 string → PHP decode-uje.
 */
const utf8ToBase64 = (str) => {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    return window.btoa(unescape(encodeURIComponent(str)));
  }
  return Buffer.from(str, 'utf8').toString('base64');
};

/**
 * Pošalje email kroz PHP backend.
 *
 * @param {object} params
 * @param {string|string[]} params.to       Primalac (ili niz)
 * @param {string} params.subject           Subject
 * @param {string} params.html              HTML body
 * @param {string} [params.text_alt]        Plaintext fallback (auto-generiše se ako fali)
 * @param {string} [params.replyTo]         Reply-To adresa
 * @param {string|string[]} [params.cc]     CC primaoci
 * @param {string|string[]} [params.bcc]    BCC primaoci
 * @param {string} [params.fromName]        Override From: name
 * @param {string} [params.fromEmail]       Override From: email (mora biti dozvoljen na SMTP-u)
 *
 * @returns {Promise<{ ok: boolean, error?: string, detail?: string }>}
 */
export const sendMail = async ({
  to,
  subject,
  html,
  text_alt,
  replyTo,
  cc,
  bcc,
  fromName,
  fromEmail,
}) => {
  if (!isMailerConfigured()) {
    const err = 'Mailer nije konfigurisan (REACT_APP_MEDIA_API_URL / TOKEN nedostaju).';
    console.warn('[mailService]', err);
    return { ok: false, error: err };
  }
  if (!to || !subject || !html) {
    const err = 'sendMail: to, subject i html su obavezni.';
    console.warn('[mailService]', err);
    return { ok: false, error: err };
  }

  try {
    const res = await fetch(`${API_URL}/mailer.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': API_TOKEN,
      },
      body: JSON.stringify({
        to,
        subject,
        html_b64: utf8ToBase64(html),
        text_alt_b64: text_alt ? utf8ToBase64(text_alt) : undefined,
        reply_to: replyTo || undefined,
        cc: cc || undefined,
        bcc: bcc || undefined,
        from_name: fromName || undefined,
        from_email: fromEmail || undefined,
      }),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json?.ok) {
      const detail = json?.detail || json?.error || `HTTP ${res.status}`;
      console.error('[mailService] Slanje neuspješno:', detail);
      return { ok: false, error: json?.error || 'Slanje neuspješno', detail };
    }
    return { ok: true };
  } catch (err) {
    console.error('[mailService] Network error:', err);
    return { ok: false, error: 'Mrežna greška', detail: err.message };
  }
};

/**
 * Pomoćnik: parsira `notificationEmails` polje iz settings.
 * Može biti:
 *   - string sa zarezima:    "info@x.com, admin@y.com"
 *   - jedan email:           "info@x.com"
 *   - undefined / prazno     -> []
 *
 * @returns {string[]} niz validnih email adresa
 */
export const parseNotificationEmails = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((s) => String(s || '').trim())
      .filter((s) => s.length > 0 && /^\S+@\S+\.\S+$/.test(s));
  }
  return String(value)
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && /^\S+@\S+\.\S+$/.test(s));
};

/**
 * "Best effort" wrapper — pošalje email i swallowuje greške (vraća bool).
 * Korisno u toku korisničkog flow-a (npr. nakon submit-a forme) gdje
 * neuspjeh notifikacije ne smije ničemu pokvariti UX.
 */
export const tryNotify = async (params) => {
  try {
    const result = await sendMail(params);
    return result.ok === true;
  } catch (err) {
    console.error('[mailService.tryNotify] swallowed error:', err);
    return false;
  }
};
